import { useEffect, useState } from "react";
import { Plus, X, Stamp, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { opsApi } from "@/operations/lib/api";

/** SUB-1 §3 — Settings › Submittals › Stamp terms. Each status maps to the stamp phrases that set
 *  it automatically on an exact match. Edits are logged. */
export default function SubmittalSettingsPage() {
  const [data, setData] = useState(null);
  const [terms, setTerms] = useState(null);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    opsApi.submittalGlobalSettings().then((d) => { setData(d); setTerms(d.stamp_terms); })
      .catch(() => toast.error("Could not load the submittal settings"));
  }, []);

  const save = async (next) => {
    setBusy(true);
    try {
      const r = await opsApi.submittalStampTerms(next);
      setTerms(r.stamp_terms);
      toast.success("Stamp terms saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const add = (status) => {
    const p = (draft[status] || "").trim();
    if (!p) return;
    const all = Object.values(terms).flat().map((x) => x.toLowerCase());
    if (all.includes(p.toLowerCase())) { toast.info(`“${p}” is already mapped`); return; }
    setDraft({ ...draft, [status]: "" });
    save({ ...terms, [status]: [...terms[status], p] });
  };
  const remove = (status, phrase) => save({ ...terms, [status]: terms[status].filter((x) => x !== phrase) });

  if (!data || !terms) return <div className="flex items-center gap-2 p-6 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  const statuses = data.statuses.filter((s) => terms[s.value]);
  const saveReview = async (patch, label) => {
    setBusy(true);
    try { const r = await opsApi.submittalReviewSettings(patch); setData({ ...data, ...r }); toast.success(label); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const rules = data.directive_rules || {};
  const toggleRule = (dir, field, value) => {
    const cur = rules[dir] || { finding_types: [], statuses: [] };
    const list = cur[field] || [];
    const next = list.includes(value) ? list.filter((x) => x !== value) : [...list, value];
    saveReview({ directive_rules: { ...rules, [dir]: { ...cur, [field]: next } } }, "Directive rules saved");
  };
  const returnStatuses = data.statuses.filter((s) => ["approved", "approved_as_noted", "revise_resubmit", "rejected", "for_record"].includes(s.value));

  return (
    <div className="page-body space-y-4 p-6" data-testid="settings-submittals">
      <div>
        <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900 md:text-lg"><Stamp className="h-4 w-4" /> Stamp terms</h2>
        <p className="mt-1 max-w-2xl text-xs text-slate-500">
          When a returned submittal's stamp text matches one of these phrases exactly, the revision's status is set
          automatically and logged as <code>stamp_match</code>. Anything else becomes a proposed status a person confirms.
        </p>
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="settings-stamp-terms">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr><th className="w-48 px-4 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Stamp phrases</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {statuses.map((s) => (
              <tr key={s.value} data-testid={`settings-stamp-row-${s.value}`}>
                <td className="px-4 py-3 align-top"><p className="font-medium text-slate-800">{s.label}</p><p className="text-[11px] text-slate-500">{s.meaning}</p></td>
                <td className="px-3 py-3">
                  <div className="flex flex-wrap gap-1.5">
                    {terms[s.value].map((p) => (
                      <span key={p} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-700">
                        {p}
                        <button onClick={() => remove(s.value, p)} disabled={busy} className="text-slate-400 hover:text-rose-600" title="Remove" data-testid={`settings-stamp-remove-${s.value}-${p.replace(/\s+/g, "-")}`}>
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                    <span className="inline-flex items-center gap-1">
                      <Input value={draft[s.value] || ""} onChange={(e) => setDraft({ ...draft, [s.value]: e.target.value })}
                             onKeyDown={(e) => { if (e.key === "Enter") add(s.value); }} placeholder="Add phrase"
                             className="h-7 w-44 text-xs" data-testid={`settings-stamp-add-${s.value}`} />
                      <Button size="sm" className="h-7 px-2" onClick={() => add(s.value)} disabled={busy} data-testid={`settings-stamp-add-btn-${s.value}`}>
                        <Plus className="h-3 w-3" />
                      </Button>
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* SUB-2 §4 — recommended-directive rules · diff sensitivity · alerts */}
      <div>
        <h2 className="text-base font-semibold text-slate-900 md:text-lg">Recommended directive</h2>
        <p className="mt-1 max-w-2xl text-xs text-slate-500">Which directive the review recommends, from the return's status and the finding types it found. Every directive still shows; only the chip moves.</p>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-xs" data-testid="settings-directive-rules">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr><th className="px-4 py-2 text-left">Directive</th>
              {returnStatuses.map((s) => <th key={s.value} className="px-2 py-2 text-center">{s.label}</th>)}
              {Object.entries(data.finding_types || {}).map(([k, l]) => <th key={k} className="px-2 py-2 text-center">{l}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {Object.entries(data.directives || {}).map(([dir, label]) => (
              <tr key={dir} data-testid={`settings-directive-row-${dir}`}>
                <td className="px-4 py-2 font-medium text-slate-800">{label}{dir === "po" && <span className="block text-[10px] font-normal text-slate-500">unless a cost-affecting finding</span>}</td>
                {returnStatuses.map((s) => (
                  <td key={s.value} className="px-2 py-2 text-center">
                    <input type="checkbox" checked={(rules[dir]?.statuses || []).includes(s.value)} disabled={busy} onChange={() => toggleRule(dir, "statuses", s.value)}
                           data-testid={`settings-rule-${dir}-status-${s.value}`} />
                  </td>
                ))}
                {Object.keys(data.finding_types || {}).map((t) => (
                  <td key={t} className="px-2 py-2 text-center">
                    <input type="checkbox" checked={(rules[dir]?.finding_types || []).includes(t)} disabled={busy} onChange={() => toggleRule(dir, "finding_types", t)}
                           data-testid={`settings-rule-${dir}-type-${t}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-sm font-semibold text-slate-800">Diff sensitivity</p>
          <p className="mt-0.5 text-xs text-slate-500">How small a changed region the visual diff reports.</p>
          <div className="mt-2 flex gap-1 rounded-lg bg-slate-100 p-0.5 text-xs">
            {["low", "medium", "high"].map((k) => (
              <button key={k} disabled={busy} onClick={() => saveReview({ diff_sensitivity: k }, `Sensitivity ${k}`)} data-testid={`settings-sensitivity-${k}`}
                      className={`flex-1 rounded-md px-2 py-1.5 capitalize ${data.diff_sensitivity === k ? "bg-white font-medium text-slate-900 shadow-sm" : "text-slate-500"}`}>{k}</button>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-sm font-semibold text-slate-800">Alerts</p>
          <p className="mt-0.5 text-xs text-slate-500">A return logged alerts the PM and PE in the app. Email is off unless switched on.</p>
          <div className="mt-2 space-y-1.5 text-xs">
            {[["pm", "Project manager"], ["pe", "Project engineer"], ["estimator_on_rr", "Scope estimator on Revise and Resubmit / Rejected"]].map(([k, l]) => (
              <label key={k} className="flex items-center gap-2">
                <input type="checkbox" checked={data.alert_recipients?.[k] !== false} disabled={busy} data-testid={`settings-alert-${k}`}
                       onChange={(e) => saveReview({ alert_recipients: { ...data.alert_recipients, [k]: e.target.checked } }, "Alert recipients saved")} /> {l}
              </label>
            ))}
            <label className="flex items-center gap-2 border-t border-slate-100 pt-1.5">
              <input type="checkbox" checked={Boolean(data.alert_email)} disabled={busy} data-testid="settings-alert-email"
                     onChange={(e) => saveReview({ alert_email: e.target.checked }, e.target.checked ? "Email alerts on" : "Email alerts off")} /> Also send an email (default off)
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
